import { PaginatedResponse } from "@/types/types";
import { User } from "@/types/user";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createIngredient, deleteIngredient, updateIngredient, restockIngredient } from "./api";
import { Ingredient } from "@/types/ingredient";

export function useCreateIngredient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: Ingredient) => createIngredient(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ingredients"] });
    },
  });
}

export function useUpdateIngredient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: Ingredient) => updateIngredient(values),

    onSuccess: (_response, values) => {
      // 1️⃣ obtener todas las queries relacionadas con "users"
      const queries = queryClient.getQueriesData<PaginatedResponse<User>>({
        queryKey: ["ingredients"],
      });

      // 2️⃣ iterar cada cache (paginaciones, filtros, etc.)
      queries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        // 3️⃣ reemplazar el usuario si coincide el id
        const updatedItems = oldData.items.map((user) =>
          user.id === values.id ? { ...user, ...values } : user,
        );

        // 4️⃣ actualizar cache
        queryClient.setQueryData(queryKey, {
          ...oldData,
          items: updatedItems,
        });
      });
    },
  });
}

export function useDeleteIngredient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: Ingredient["id"]) => deleteIngredient(id),

    onSuccess: (_response, id) => {
      const queries = queryClient.getQueriesData<PaginatedResponse<Ingredient>>({
        queryKey: ["ingredients"],
      });

      queries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        const updatedItems = oldData.items.filter((client) => client.id !== id);

        queryClient.setQueryData(queryKey, {
          ...oldData,
          items: updatedItems,
        });
      });
    },
  });
}

export function useRestockIngredient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ingredientId,
      quantity,
    }: {
      ingredientId: string;
      quantity: number;
    }) => restockIngredient(ingredientId, quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ingredients"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

